import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ConfigurationItem, NetworkConstants, Person } from 'diwimamodelweb/lib';
import { PersonForm } from '../model/form';
import { PersonService } from '../person.service';
import { UserFeedbackService } from '../user-feedback.service';
import { GenericDialogComponent } from '../generic-dialog/generic-dialog.component'
import { TranslateService } from '@ngx-translate/core';
import { deepCopy, diffObject } from 'diwimamodelweb/lib/utils/obejct-diff';
import { SettingsService } from '../settings.service';

const buildPersons = (oldPersonData: Person|null, formValue: any, reportGroupOptions: ConfigurationItem[]): [Person|null, Person] => {
  const oldPerson = (oldPersonData) ? deepCopy(oldPersonData, oldPersonData.replacerFn) : null
  var newPerson = Object.assign({}, oldPersonData)
  const {vorname, nachname, anzeigeName, 
    geschlecht, anrede, titelAkademisch, 
    titelAndere, hundeJagdlichImTreiben, 
    hundeNachsucheGespann, emailAddresse, telefon, kommentar,
    zip, address1, address2, city, reportGroupId
  } = formValue
  newPerson.vorname = vorname
  newPerson.nachname = nachname
  newPerson.anzeigeName = anzeigeName
  newPerson.geschlecht = geschlecht
  newPerson.anrede = anrede
  newPerson.titelAkademisch = titelAkademisch
  newPerson.titelAndere = titelAndere
  newPerson.reportGroupId = reportGroupId
  if (reportGroupId != null) {
    const option = reportGroupOptions?.find(x => x.identifier == reportGroupId)
    if (option) newPerson.reportGroupTitle = option.title
  }
  newPerson.hundeJagdlichImTreiben = hundeJagdlichImTreiben
  newPerson.hundeNachsucheGespann = hundeNachsucheGespann
  newPerson.emailAddresse = emailAddresse
  newPerson.telefon = telefon
  newPerson.kommentar = kommentar


  // set nested object kvalues
  if (!newPerson.legalPersonalAddress) newPerson.legalPersonalAddress = {
    zip: null,
    address1: null,
    city: null
  }
  if (oldPerson?.legalPersonalAddress) {
    newPerson.legalPersonalAddress = Object.assign(newPerson.legalPersonalAddress, oldPerson.legalPersonalAddress)
  }
  newPerson.legalPersonalAddress.zip = zip
  newPerson.legalPersonalAddress.address1 = address1
  newPerson.legalPersonalAddress.address2 = address2
  newPerson.legalPersonalAddress.city = city
  if (oldPerson?.billingAddress) {
    newPerson.billingAddress = Object.assign({}, oldPerson.billingAddress)
  }
  return [oldPerson, newPerson]
}

@Component({
  selector: 'app-person-form',
  templateUrl: './person-form.component.html',
  styleUrls: ['./person-form.component.css', '../../app.component.scss']
})
export class PersonFormComponent implements OnInit {

  @Input() data: PersonForm
  @Input() formBuilder: FormBuilder
  @Input() userFeedbackService: UserFeedbackService
  @Input() objectService: PersonService
  @Input() translateService: TranslateService
  @Input() setSettingsService(service: SettingsService) {
    this.settingsService = service
    this.reportGroupOptions = this.settingsService.settingReadoutGroup.value.values
  }

  settingsService: SettingsService

  submitted = false
  reportGroupOptions: ConfigurationItem[]
  isNewPerson = false
  personForm: FormGroup

  personCreateRight = false
  personUpdateRight = false
  personDeleteRight = false

  get hasCrudRight(): boolean {
    return this.isNewPerson ? this.personCreateRight : this.personUpdateRight
  }

  get canSave(): boolean {
    return this.hasCrudRight && !this.personForm?.invalid && !this.submitted
  }

  get canDelete(): boolean {
    return this.personDeleteRight && !this.isNewPerson && !this.submitted
  }

  private loadCrudRights(): void {
    const ctxId = this.settingsService?.selectedUserContext$?.value?.identifier
    const rights = ctxId
      ? this.settingsService.rightsPercontext?.get(ctxId)?.[NetworkConstants.COLLECTION_PERSON]
      : undefined
    this.personCreateRight = !!rights?.['C']
    this.personUpdateRight = !!rights?.['U']
    this.personDeleteRight = !!rights?.['D']
  }
  
  selectedNachnameValue: string;
  selectedVornameValue: string;

  constructor() {
    this.selectedNachnameValue=""
    this.selectedVornameValue=""
  }

  ngOnInit(): void {
    this.loadCrudRights()
	  if (this.data.data) {
      this.isNewPerson=false
      const {
        vorname,
        nachname, 
        anzeigeName,
        geschlecht,
        anrede,
        titelAkademisch,
        titelAndere,
        hundeJagdlichImTreiben,
        hundeNachsucheGespann,
        emailAddresse,
        telefon,
        kommentar,
        reportGroupId,
        legalPersonalAddress
      } = !this.data.data ? {} as any : this.data.data
      
      var { address1, address2, zip, city } = legalPersonalAddress ?? {}
      this.personForm = this.formBuilder.group({
        vorname: [vorname],
        nachname: [nachname],  
        anzeigeName: [anzeigeName],
        geschlecht: [geschlecht],
        anrede: [anrede],
        titelAkademisch: [titelAkademisch],
        titelAndere: [titelAndere],
        hundeJagdlichImTreiben: [hundeJagdlichImTreiben],
        hundeNachsucheGespann: [hundeNachsucheGespann],
        // legalPersonalAddress: [],
        // billingAddress: [],
        reportGroupId: [reportGroupId],
        address1: [{ value: address1 ? address1 : undefined, disabled: false }],
        address2: [{ value: address2 ? address2 : undefined, disabled: false }],
        zip:[{ value: zip ? zip : undefined, disabled: false }],
        city: [{ value: city ? city : undefined, disabled: false}],
        
        emailAddresse: [emailAddresse],  
        telefon: [telefon],  
        kommentar: [kommentar]
      })

           
    } else {
      this.isNewPerson = true
      this.personForm = this.formBuilder.group({
        vorname: [],
        nachname: [],  
        anzeigeName: [, Validators.required],
        geschlecht: [],
        anrede: [],
        titelAkademisch: [],
        titelAndere: [],
        hundeJagdlichImTreiben: [],
        hundeNachsucheGespann: [],
        // legalPersonalAddress: [],
        // billingAddress: [],
        reportGroupId: [],
        address1: [],
        address2: [],
        zip:[],
        city: [],
        
        emailAddresse: [],  
        telefon: [],  
        kommentar: []
      })
    }

    //everytime the lastname field changes it would update the displayname field
      this.personForm.get("nachname").valueChanges.subscribe( selectedNachnameValue =>{
        //on the condition we make sure that if the first name field is empty it will not show on the display the word null
        //to achieve this if the other field is null the display field only shows the lastname value
        if(!this.selectedVornameValue && !this.personForm.value.vorname){
          this.personForm.get("anzeigeName").setValue( selectedNachnameValue)
        }else{
          this.personForm.get("anzeigeName").setValue(this.personForm.value.vorname + " " + selectedNachnameValue)
        }
      })

    //everytime the firstname field changes it would update the displayname field
      this.personForm.get("vorname").valueChanges.subscribe( selectedVornameValue =>{
        //on the condition we make sure that if the lastName field is empty it will not show on the display the word null
        //to achieve this if the other field is null the display field only shows the firstname value
        if(!this.selectedNachnameValue && !this.personForm.value.nachname){
          this.personForm.get("anzeigeName").setValue(selectedVornameValue)
        }else{
          this.personForm.get("anzeigeName").setValue(selectedVornameValue  + " " +  this.personForm.value.nachname)
        }
      })

    this.data.onNavBack = this.onCancle
  }

  async submit() {
    if (!this.hasCrudRight) {
      const create = this.translateService.instant("P_not_allowed_create")
      const edit = this.translateService.instant("P_not_allowed_edit")
      this.userFeedbackService.showSnackBar.emit(
        this.isNewPerson ? create : edit,
      )
      return
    }
    const [oldPerson, newPerson] = buildPersons(this.data.data, this.personForm.value, this.reportGroupOptions)
    // todo create or update... 
    if (!oldPerson) {
      // create
      // this.isNewPerson=true
      try {
        this.userFeedbackService.loading.emit(true)
        await this.objectService.createPerson(newPerson) 
        this.userFeedbackService.loading.emit(false)
        const sbSuccessSave = await this.translateService.instant('successfully_created')
        this.userFeedbackService.showSnackBar.emit(sbSuccessSave)
        this.data.onSuccessfullySubmitted()
      } catch (error) {
        this.userFeedbackService.loading.emit(false)
        this.userFeedbackService.showDialog.emit(
          {
            entryComponent: GenericDialogComponent,
            title: "Error",
            message: error.error.message ? error.error.message : JSON.stringify(error),
          }
        )
      }
    } else {
      // update
      try {
        this.userFeedbackService.loading.emit(true)
        await this.objectService.updatePerson(oldPerson, newPerson)
        this.userFeedbackService.loading.emit(false)
        const sbSuccessSave = await this.translateService.instant('changes_saved')
        this.userFeedbackService.showSnackBar.emit(sbSuccessSave)
        this.data.onSuccessfullySubmitted()
      } catch (error) {
        this.userFeedbackService.loading.emit(false)
        this.userFeedbackService.showDialog.emit(
          {
            entryComponent: GenericDialogComponent,
            title: "Error",
            message: error.error.message ? error.error.message : JSON.stringify(error),
          }
        )
      }

    }
  }
  onCancle = async () => {
    const [oldPerson, newPerson] = buildPersons(this.data.data, this.personForm.value, this.reportGroupOptions)
    if (!oldPerson) {

      const title = this.translateService.instant('title_unsaved_changes')
      const message = this.translateService.instant('msg_unsaved_changes')
      this.userFeedbackService.showDialog.emit(
        {
          entryComponent: GenericDialogComponent,
          title,
          message,
          cancleCallback: async () => {
            //this gets the textvalue from en.json
            const sbChangesDismissed = this.translateService.instant('changes_dismissed')
            this.userFeedbackService.showSnackBar.emit(sbChangesDismissed)
            this.data.onCancle()
          },
          positiveCallback: async ()=> {
            await this.submit()
            this.data.onSuccessfullySubmitted()
          }
        }
      )



    } else {
      const diff = diffObject(oldPerson, newPerson, {})
      if (Object.keys(diff).length == 0) {
        this.data.onCancle()
      }
      else {
        // show alert
        const title = await this.translateService.instant('title_unsaved_changes')
        const message = await this.translateService.instant('msg_unsaved_changes')
        this.userFeedbackService.showDialog.emit(
          {
            entryComponent: GenericDialogComponent,
            title,
            message,
            cancleCallback: async () => {
              const sbChangesDismissed = await this.translateService.instant('changes_dismissed')
              this.userFeedbackService.showSnackBar.emit(sbChangesDismissed)
              this.data.onCancle()
            },
            positiveCallback: async ()=> {
              await this.submit()
              this.data.onSuccessfullySubmitted()
            }
          }
        )
      }
    }
  }


  async onDelete(){
    if (!this.canDelete) return

    if(!this.isNewPerson){
      const title = await this.translateService.instant('delete_title')
      const message = await this.translateService.instant('delete_message')
      this.userFeedbackService.loading.emit(false)
      this.userFeedbackService.showDialog.emit(
        {
          entryComponent: GenericDialogComponent,
          title: title,
          message: message,
          cancleCallback: async ()=> {
            const sbCancelDelete = await this.translateService.instant('delete_canceled')
            this.userFeedbackService.showSnackBar.emit(sbCancelDelete)
          },
          positiveCallback: async ()=>{
            try{
              this.userFeedbackService.loading.emit(true)
              const sbSuccessDelete = await this.translateService.instant('delete_completed')
              await this.objectService.deletePerson(this.data.data)
              this.userFeedbackService.loading.emit(false)
              this.userFeedbackService.showSnackBar.emit(sbSuccessDelete)
              this.data.onCancle()
            }catch(errorDelete){
              console.log(errorDelete)
              const sbErrorDelete = await this.translateService.instant('delete_error')
              this.userFeedbackService.showSnackBar.emit(sbErrorDelete)

            }
          } 
        }
      )

    }else{
      this.userFeedbackService.loading.emit(true)
      const sbSNoDelete = await this.translateService.instant('delete_not_possible')
      this.userFeedbackService.showSnackBar.emit(sbSNoDelete) 
    }
  }
}
